package sarka.design.skoria.webrtc

import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.w3c.dom.mediacapture.MediaStream
import org.w3c.dom.mediacapture.MediaStreamTrack

actual class PlatformVideoTrack internal constructor(
    internal val native: MediaStreamTrack
)

actual class WebRtcManager actual constructor(
    private val onSendSdp: (String) -> Unit,
    private val onSendIceCandidate: (String) -> Unit,
) {
    private val json = Json { ignoreUnknownKeys = true }

    private val _local = MutableStateFlow<PlatformVideoTrack?>(null)
    actual val localVideoTrack: StateFlow<PlatformVideoTrack?> = _local

    private val _remote = MutableStateFlow<PlatformVideoTrack?>(null)
    actual val remoteVideoTrack: StateFlow<PlatformVideoTrack?> = _remote

    private val _audioEnabled = MutableStateFlow(true)
    actual val audioEnabled: StateFlow<Boolean> = _audioEnabled

    private val pendingRemoteIce = mutableListOf<IceCandidatePayload>()

    private var localStream: MediaStream? = null
    private var localAudioTrack: MediaStreamTrack? = null
    private var pc: dynamic = null

    // glare handling
    private var makingOffer: Boolean = false
    private var polite: Boolean = true // set this depending on your pairing rules if you want

    private var localTracksAdded = false
    private var pendingOfferJson: String? = null

    /**
     * Call this if you can decide initiator/role from server:
     * - initiator should be "impolite" (wins offers)
     * - non-initiator should be "polite"
     */
    actual fun setPolite(isPolite: Boolean) {
        polite = isPolite
    }

    actual fun resetForNewPeer() {
        console.log("WebRTC: resetForNewPeer()")

        // close old pc
        try {
            pc?.onicecandidate = null
            pc?.ontrack = null
            pc?.onconnectionstatechange = null
            pc?.onsignalingstatechange = null
            pc?.close()
        } catch (_: dynamic) {}
        pc = null

        // reset state
        pendingRemoteIce.clear()
        pendingOfferJson = null
        makingOffer = false

        // clear remote view
        _remote.value = null

        // create a brand new pc + re-add local tracks
        ensurePeerConnection()

        val stream = localStream
        if (stream != null) {
            // IMPORTANT: add local tracks to the *new* pc
            stream.getTracks().forEach { tr ->
                pc.addTrack(tr, stream)
            }
            localTracksAdded = true
        } else {
            localTracksAdded = false
        }
    }

    actual fun start() {
        val mediaDevices = window.navigator.mediaDevices
        if (mediaDevices == null) {
            console.error("mediaDevices is null. Use https:// or http://localhost")
            return
        }

        val constraints = js("""
            ({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
              },
              video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }
            })
        """)

        mediaDevices.getUserMedia(constraints)
            .then { stream ->
                localStream = stream

                val videoTracks = stream.getVideoTracks()
                val t = if (videoTracks.length > 0) videoTracks[0] else null
                if (t == null) {
                    console.error("No video track from getUserMedia")
                    _local.value = null
                } else {
                    _local.value = PlatformVideoTrack(t)
                }

                // Store reference to audio track for mute/unmute
                val audioTracks = stream.getAudioTracks()
                localAudioTrack = if (audioTracks.length > 0) audioTracks[0] else null

                ensurePeerConnection()
                // Add local tracks to PC
                stream.getTracks().forEach { tr ->
                    pc.addTrack(tr, stream)
                }

                localTracksAdded = true

                // If an offer arrived before we had tracks, process it now
                pendingOfferJson?.let { offerJson ->
                    pendingOfferJson = null
                    onRemoteSdp(offerJson)
                }

                stream
            }
            .catch { err ->
                console.error("getUserMedia failed", err)
            }
    }

    actual fun stop() {
        try {
            pc?.onicecandidate = null
            pc?.ontrack = null
            pc?.onconnectionstatechange = null
            pc?.onsignalingstatechange = null
            pc?.close()
        } catch (_: dynamic) {}
        pc = null

        localStream?.getTracks()?.forEach { it.stop() }
        localStream = null
        localAudioTrack = null

        _local.value = null
        _remote.value = null
        _audioEnabled.value = true
    }

    private fun ensurePeerConnection() {
        if (pc != null) return

        val config = js("""
            ({
              iceServers: [
                { urls: "stun:stun.l.google.com:19302" },
                { urls: "stun:stun1.l.google.com:19302" }
              ]
            })
        """)

        pc = js("new RTCPeerConnection(config)")

        pc.onicecandidate = { event: dynamic ->
            val cand = event.candidate
            if (cand != null) {
                val payload = IceCandidatePayload(
                    candidate = cand.candidate as String,
                    sdpMid = (cand.sdpMid as String?) ?: "",
                    sdpMLineIndex = (cand.sdpMLineIndex as Int?) ?: 0
                )
                onSendIceCandidate(json.encodeToString(payload))
            }
        }

        pc.ontrack = { event: dynamic ->
            // event.track is a MediaStreamTrack
            val track = event.track.unsafeCast<MediaStreamTrack>()
            console.log("ONTRACK", track.kind)
            if (track.kind == "video") {
                _remote.value = PlatformVideoTrack(track)
            } else if (track.kind == "audio") {
                // Attach remote audio to an <audio> element so it plays through speakers
                val audio = js("document.createElement('audio')")
                audio.autoplay = true
                audio.srcObject = js("new MediaStream([event.track])")
                js("document.body.appendChild(audio)")
            }
        }

        pc.onconnectionstatechange = {
            console.log("pc.connectionState=", pc.connectionState)
        }
        pc.onsignalingstatechange = {
            console.log("pc.signalingState=", pc.signalingState)
        }
    }

    /**
     * Initiator calls this after pairing.
     * Other side should NOT call createOffer at the same time,
     * unless you implement full “perfect negotiation” on both sides.
     */
    actual fun createOffer() {
        ensurePeerConnection()
        val pcLocal = pc ?: return

        makingOffer = true
        val offerOptions = js("""({ offerToReceiveVideo: true, offerToReceiveAudio: true })""")

        pcLocal.createOffer(offerOptions)
            .then { offer: dynamic ->
                // IMPORTANT: return the promise
                pcLocal.setLocalDescription(offer)
            }
            .then {
                val localDesc = pcLocal.localDescription
                if (localDesc == null) {
                    console.error("createOffer: localDescription is null")
                    return@then js("undefined")
                }
                val payload = SdpPayload(
                    type = localDesc.type as String,
                    sdp = localDesc.sdp as String
                )
                onSendSdp(json.encodeToString(payload))
                js("undefined")
            }
            .catch { err: dynamic ->
                console.error("createOffer failed", err)
            }
            .finally {
                makingOffer = false
            }
    }

    actual fun setAudioEnabled(enabled: Boolean) {
        localAudioTrack?.enabled = enabled
        _audioEnabled.value = enabled
    }

    /**
     * payload is JSON string of SdpPayload.
     * If it’s an offer -> setRemoteDescription -> createAnswer -> setLocalDescription -> send answer
     * If it’s an answer -> just setRemoteDescription
     */
    actual fun onRemoteSdp(payload: String) {
        ensurePeerConnection()
        val pcLocal = pc ?: return

        val sdpPayload = try {
            json.decodeFromString<SdpPayload>(payload)
        } catch (e: Throwable) {
            console.error("onRemoteSdp: invalid json", e)
            return
        }

        val desc = rtcDesc(sdpPayload.type, sdpPayload.sdp)

        if (sdpPayload.type == "offer" && !localTracksAdded) {
            console.log("Deferring offer handling until local tracks are added")
            pendingOfferJson = payload
            return
        }

        pcLocal.setRemoteDescription(desc)
            .then {
                flushPendingIce()

                if (sdpPayload.type == "offer") {
                    // IMPORTANT: return chain
                    pcLocal.createAnswer()
                        .then { answer: dynamic ->
                            pcLocal.setLocalDescription(answer) // return promise
                        }
                        .then {
                            val localDesc = pcLocal.localDescription
                            if (localDesc == null) {
                                console.error("onRemoteSdp: localDescription is null after answer")
                                return@then js("undefined")
                            }
                            val answerPayload = SdpPayload(
                                type = localDesc.type as String,
                                sdp = localDesc.sdp as String
                            )
                            onSendSdp(json.encodeToString(answerPayload))
                            js("undefined")
                        }
                } else {
                    js("undefined")
                }
            }
            .catch { err: dynamic ->
                console.error("setRemoteDescription/createAnswer failed", err, "type=", sdpPayload.type)
            }
    }

    /**
     * payload is JSON string of IceCandidatePayload.
     */
    actual fun onRemoteIceCandidate(payload: String) {
        ensurePeerConnection()
        val pcLocal = pc ?: return

        val ice = try {
            json.decodeFromString<IceCandidatePayload>(payload)
        } catch (e: Throwable) {
            console.error("onRemoteIceCandidate: invalid json", e)
            return
        }

        // queue until remoteDescription is set
        if (pcLocal.remoteDescription == null) {
            pendingRemoteIce.add(ice)
            return
        }

        val candidate = js("({})")
        candidate.candidate = ice.candidate
        candidate.sdpMid = ice.sdpMid
        candidate.sdpMLineIndex = ice.sdpMLineIndex

        pcLocal.addIceCandidate(candidate)
            .catch { err: dynamic -> console.error("addIceCandidate failed", err) }
    }

    private fun flushPendingIce() {
        val pcLocal = pc ?: return
        if (pcLocal.remoteDescription == null) return
        if (pendingRemoteIce.isEmpty()) return

        val list = pendingRemoteIce.toList()
        pendingRemoteIce.clear()
        list.forEach { ice ->
            val c = js("({})")
            c.candidate = ice.candidate
            c.sdpMid = ice.sdpMid
            c.sdpMLineIndex = ice.sdpMLineIndex
            pcLocal.addIceCandidate(c).catch { err: dynamic -> console.error("addIceCandidate failed", err) }
        }
    }


    private fun rtcDesc(type: String, sdp: String): dynamic {
        val o = js("({})")
        o.type = type
        o.sdp = sdp
        return o
    }
}
